import AHBoolean from "common/values/boolean/boolean";
import Date from "common/values/date/date";
import Guid from "common/values/guid/guid";
import Name from "common/values/name/name";
import Percent from "common/values/percent/percent";
import moment from "moment";
import User from "users/entities/user/user";
import EntityClientRepresentative from "work/entities/entity-client-representative/entity-client-representative";
import EntityRepresentativeAPIResponse
  from "work/entities/entity-representative/api/response-contracts/entity-representative-api-response";
import WorkVendorRepresentativeAPIResponse
  from "work/entities/entity-vendor-representative/api/response-contracts/work-vendor-representative-api-response";
import ProposalReviewerAPIResponse from "work/entities/proposal/api/response-contracts/proposal-reviewer-api-response";
import ReplaceableDocumentAPIResponse
, { MaskableDocuments } from "work/entities/proposal/api/response-contracts/replaceable-document-api-response";
import WorkCreatorInfoAPIResponse from "work/entities/proposal/api/response-contracts/work-creator-info-api-response";
import Proposal, { CompleteProposalSpec, IProposalStore, ProposalMetaInfo, } from "work/entities/proposal/proposal";
import {
  IWorkAgreementStore,
  Masked,
  MaskedContracts, MaskedDiscount,
  MaskedFeeSchedule,
  MaskedTeam
} from "work/entities/work-agreement/work-agreement";
import { ProposalAction, ProposalStatus } from "work/values/constants";
import ProjectDescription from "work/values/project-description/project-description";
import ProjectName from "work/values/project-name/project-name";
import DetailedWorkVendorTeamAPIResponse
  from "work/values/vendor-team/api/response-contracts/detailed-work-vendor-team-api-response";
import SimpleWorkVendorTeamAPIResponse
  from "work/values/vendor-team/api/response-contracts/simple-work-vendor-team-api-response";
import DetailedVendorTeam from "work/values/vendor-team/detailed-vendor-team";
import VendorTeam from "work/values/vendor-team/vendor-team";
import WorkDocument, { WorkDocumentType } from "work/values/work-document/work-document";
import WorkFeeScheduleAPIResponse from "work/values/fee-schedule/api/response-contracts/work-fee-schedule-api-response";
import FeeScheduleCategory, {
  FeeScheduleCategoryMaskedCurrency
} from "work/values/fee-schedule-category/fee-schedule-category";

export default class WorkProposalAPIResponse {
  id?: string;
  RFPId?: string;
  creator?: WorkCreatorInfoAPIResponse;
  createdDate?: string;
  lastUpdated?: string;
  name?: string;
  description?: string;

  client?: EntityRepresentativeAPIResponse;
  vendors: WorkVendorRepresentativeAPIResponse[] = [];
  vendorTeam?: SimpleWorkVendorTeamAPIResponse | DetailedWorkVendorTeamAPIResponse;
  feeSchedule?: WorkFeeScheduleAPIResponse;
  startDate?: string;
  endDate?: string;
  discount?: {
    isMasked: boolean;
    percent: number;
  };

  clientTeamTemplateIds?: string[];
  vendorTeamTemplateIds?: string[];
  clientFeeScheduleTemplateIds?: string[];
  vendorFeeScheduleTemplateIds?: string[];

  clientPolicies?: MaskableDocuments;
  vendorPolicies?: MaskableDocuments;
  conflicts?: MaskableDocuments;

  conflictsCheckWaived?: {
    isMasked: boolean;
    value: boolean;
  };
  teamRestricted?: {
    isMasked: boolean;
    value: boolean;
  };

  archiveDate?: string;
  hireDate?: string;
  negotiable: boolean = true;
  responseDueBy?: string;

  supersedes?: WorkProposalAPIResponse;
  supersededById?: string;
  replacedBy?: string;
  workAgreementId?: string;

  status?:
    | "AwaitingSubmission"
    | "AwaitingApprovalByClient"
    | "AwaitingApprovalByTeamLeader"
    | "AwaitingApprovalByVendors"
    | "AwaitingApprovalByTeam"
    | "AwaitingHire"
    | "RevisionRequested"
    | "Archived";
  availableActions?: { [key: string]: string[] };
  clientReviewers?: ProposalReviewerAPIResponse[];
  vendorReviewers?: ProposalReviewerAPIResponse[];

  deserialize(
    proposalApiService: IProposalStore,
    workAgreementApiService: IWorkAgreementStore,
    currentUser: User,
    team?: VendorTeam | DetailedVendorTeam | MaskedTeam,
    withRedline: boolean = true
  ): Proposal {
    const spec: CompleteProposalSpec = this.constructProposalSpec(team instanceof VendorTeam ? undefined : team);
    const metaInfo: ProposalMetaInfo =
      this.constructProposalMetaInfo();
    const proposal = new Proposal(
      proposalApiService,
      workAgreementApiService,
      currentUser,
      spec,
      undefined,
      metaInfo,
      undefined,
      withRedline
    );

    proposal.vendorTeam = proposal.vendorTeam ?? team ?? null;

    return proposal;
  }

  private constructProposalMetaInfo(): ProposalMetaInfo {
    if (!this.id)
      throw new InvalidProposalAPIResponse("Proposal id not returned by api.");
    if (!this.creator?.userId)
      throw new InvalidProposalAPIResponse(
        "Proposal creator not returned by api."
      );
    if (!this.creator?.entityId)
      throw new InvalidProposalAPIResponse(
        "Proposal creator entity id not returned by api."
      );
    if (!this.client?.userId)
      throw new InvalidProposalAPIResponse("Client not returned by api.");
    if (!this.client?.entityId)
      throw new InvalidProposalAPIResponse(
        "Client entity id not returned by api."
      );
    if (this.availableActions === undefined)
      throw new InvalidProposalAPIResponse(
        "Available actions not returned by api."
      );

    const metaInfo: ProposalMetaInfo = {
      RFPId: this.RFPId ? new Guid(this.RFPId) : undefined,
      id: new Guid(this.id),
      workAgreementId: this.workAgreementId
        ? new Guid(this.workAgreementId)
        : undefined,
      creator: new EntityClientRepresentative(
        new Guid(this.creator.userId),
        new Guid(this.creator.entityId),
        new Name(
          this.creator.firstName,
          this.creator.lastName
        )
      ),
      creatorInfo: Object.assign(
        new WorkCreatorInfoAPIResponse(),
        this.creator
      ).deserialize(),
      status: ProposalStatus[this.status as keyof typeof ProposalStatus],
      createdDate: new Date(moment(this.createdDate)),
      lastUpdated: new Date(moment(this.lastUpdated)),
      archiveDate: this.archiveDate
        ? new Date(moment(this.archiveDate))
        : undefined,
      hireDate: this.hireDate
        ? new Date(moment(this.hireDate))
        : undefined,
      availableActions: {
        [ProposalAction.Submit]: [],
        [ProposalAction.Approve]: [],
        [ProposalAction.Reject]: [],
        [ProposalAction.Revise]: [],
        [ProposalAction.Hire]: [],
        [ProposalAction.Delete]: [],
        [ProposalAction.Cancel]: [],
        [ProposalAction.Edit]: [],
        [ProposalAction.Review]: [],
      },
      supersededById: this.supersededById
        ? new Guid(this.supersededById)
        : undefined,
      replacedBy: this.replacedBy ? new Guid(this.replacedBy) : undefined,
    };

    if (this.availableActions) {
      for (const [action, userIds] of Object.entries(this.availableActions)) {
        metaInfo.availableActions[
          ProposalAction[action as keyof typeof ProposalAction]
        ] = userIds.map((userId: string) => new Guid(userId));
      }
    }

    return metaInfo;
  }

  protected constructProposalSpec(team?: DetailedVendorTeam | MaskedTeam): CompleteProposalSpec {
    if (!this.client?.userId)
      throw new InvalidProposalAPIResponse("Client not returned by api.");
    if (!this.client?.entityId)
      throw new InvalidProposalAPIResponse(
        "Client entity id not returned by api."
      );

    let conflictsCheckWaived: AHBoolean | MaskedContracts;
    if (this.conflictsCheckWaived === undefined)
      conflictsCheckWaived = new AHBoolean(false);
    else if (this.conflictsCheckWaived.isMasked) {
      conflictsCheckWaived = new MaskedContracts();
    } else {
      conflictsCheckWaived = new AHBoolean(this.conflictsCheckWaived.value);
    }

    let teamRestricted: AHBoolean | MaskedTeam;
    if (this.teamRestricted === undefined)
      teamRestricted = new AHBoolean(false);
    else if (this.teamRestricted.isMasked) {
      teamRestricted = new MaskedTeam();
    } else {
      teamRestricted = new AHBoolean(this.teamRestricted.value);
    }

    let clientPolicyDocuments: WorkDocument[] | MaskedContracts;
    if (this.clientPolicies === undefined) {
      clientPolicyDocuments = [];
    } else if (this.clientPolicies.isMasked) {
      clientPolicyDocuments = new MaskedContracts();
    } else {
      clientPolicyDocuments = this.clientPolicies?.documents?.filter(
        (document) => document.documentInfo).map(
          (document) => Object.assign(new ReplaceableDocumentAPIResponse(), document)
            .deserialize(WorkDocumentType.ClientPolicy)) ?? [];
    }
    let vendorPolicyDocuments: WorkDocument[] | MaskedContracts;
    if (this.vendorPolicies === undefined) {
      vendorPolicyDocuments = [];
    } else if (this.vendorPolicies.isMasked) {
      vendorPolicyDocuments = new MaskedContracts();
    } else {
      vendorPolicyDocuments = this.vendorPolicies?.documents?.filter(
        (document) => document.documentInfo).map(
          (document) => Object.assign(new ReplaceableDocumentAPIResponse(), document)
            .deserialize(WorkDocumentType.VendorPolicy)) ?? [];
    }

    let conflictsDocuments: WorkDocument[] | MaskedContracts;
    if (this.conflicts === undefined) {
      conflictsDocuments = [];
    } else if (this.conflicts.isMasked) {
      conflictsDocuments = new MaskedContracts();
    } else {
      conflictsDocuments = this.conflicts?.documents?.filter(
        (document) => document.documentInfo).map(
          (document) => Object.assign(new ReplaceableDocumentAPIResponse(), document)
            .deserialize(WorkDocumentType.Conflicts)) ?? [];
    }

    let feeSchedule: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule;
    if (this.feeSchedule === undefined) {
      feeSchedule = [];
    } else {
      const completeFeeSchedule = Object.assign(
        new WorkFeeScheduleAPIResponse(), this.feeSchedule).deserialize();
      feeSchedule = Masked.isMasked(completeFeeSchedule) ?
        completeFeeSchedule :
        completeFeeSchedule.categories ?? [];
    }

    return {
      client: new EntityClientRepresentative(
        new Guid(this.client.userId),
        new Guid(this.client.entityId),
        new Name(
          this.client.firstName,
          this.client.lastName
        )
      ),
      name: new ProjectName(this.name ?? ""),
      description: new ProjectDescription(this.description ?? ""),
      negotiable: this.negotiable,
      responseDueBy: moment(this.responseDueBy).isValid()
        ? new Date(moment(this.responseDueBy))
        : undefined,
      startDate: moment(this.startDate).isValid()
        ? new Date(moment(this.startDate))
        : undefined,
      endDate: moment(this.endDate).isValid()
        ? new Date(moment(this.endDate))
        : undefined,
      clientReviewers:
        this.clientReviewers?.map((reviewer) =>
          Object.assign(
            new ProposalReviewerAPIResponse(),
            reviewer
          ).deserialize()
        ) ?? [],
      vendorReviewers:
        this.vendorReviewers?.map((reviewer) =>
          Object.assign(
            new ProposalReviewerAPIResponse(),
            reviewer
          ).deserialize()
        ) ?? [],
      conflictsCheckWaived: conflictsCheckWaived,
      teamRestricted: teamRestricted,
      clientTeamTemplateIds:
        this.clientTeamTemplateIds?.map((id) => new Guid(id)) ?? [],
      vendorTeamTemplateIds:
        this.vendorTeamTemplateIds?.map((id) => new Guid(id)) ?? [],
      clientFeeScheduleTemplateIds:
        this.clientFeeScheduleTemplateIds?.map((id) => new Guid(id)) ?? [],
      vendorFeeScheduleTemplateIds:
        this.vendorFeeScheduleTemplateIds?.map((id) => new Guid(id)) ?? [],
      clientPolicyDocuments: clientPolicyDocuments,
      vendorPolicyDocuments: vendorPolicyDocuments,
      conflictsDocuments: conflictsDocuments,
      discount: this.discount?.isMasked ? new MaskedDiscount() : new Percent(this.discount?.percent ?? 0),
      vendorTeam: team,
      feeSchedule: feeSchedule,
      RFPId: this.RFPId ? new Guid(this.RFPId) : undefined,
      vendors:
        this.vendors?.map((vendor) =>
          Object.assign(
            new WorkVendorRepresentativeAPIResponse(),
            vendor
          ).deserialize()
        ) ?? [],
    };
  }
}

export class InvalidProposalAPIResponse extends Error { }
